/**
 * Phase 15.6M — Frequent Collaborators section for artist profiles.
 *
 * Shows the artists this user collaborates with most often (both as
 * primary artist with featured guests, and as a featured guest on
 * others' tracks). Hidden when there are zero collaborations.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import PresenceDot from "@/components/PresenceDot";
import { optimizeImage } from "@/lib/mediaUrls";

const FrequentCollaborators = ({ userId, limit = 8 }) => {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/users/${userId}/frequent-collaborators`, { params: { limit } });
        if (!cancelled) {
          setCollabs(r.data?.collaborators || []);
          setLoading(false);
        }
      } catch (_e) {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, limit]);

  if (loading) return null;
  if (collabs.length === 0) return null;

  return (
    <Card
      className="p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/10 border-purple-500/30 mb-4"
      data-testid="frequent-collaborators"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-white font-bold text-base flex items-center gap-2">
          <span>🤝</span> Frequent Collaborators
        </h3>
        <span className="text-[11px] text-purple-200/60">Top {collabs.length}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }} data-testid="frequent-collaborators-scroller">
        {collabs.map((c) => (
          <Link
            key={c.user_id}
            to={`/artist/${c.user_id}`}
            className="group flex-shrink-0 w-20 text-center"
            data-testid={`frequent-collab-${c.user_id}`}
            title={`${c.collab_count} collab${c.collab_count > 1 ? "s" : ""}`}
          >
            <div className="relative w-16 h-16 mx-auto rounded-full overflow-hidden border-2 border-purple-500/40 group-hover:border-pink-400 group-hover:scale-105 transition-all">
              {c.profile_photo ? (
                <img
                  src={optimizeImage(c.profile_photo, 'avatar')}
                  alt={c.artist_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-pink-700 text-white font-bold text-lg">
                  {(c.artist_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="absolute -bottom-0.5 right-0">
                <PresenceDot userId={c.user_id} size="md" />
              </div>
            </div>
            <p className="mt-1 text-[11px] text-white truncate group-hover:text-pink-300">{c.artist_name}</p>
            <p className="text-[10px] text-purple-200/60 tabular-nums">{c.collab_count} track{c.collab_count !== 1 ? "s" : ""}</p>
          </Link>
        ))}
      </div>
    </Card>
  );
};

export default FrequentCollaborators;
